import type { ComponentType } from 'react';

// Every project page of the old platform (frontend/src/main.jsx), minus the
// dropped ones (docs/architecture/03-roadmap.md §5): Flow and flow-embed,
// Benchmark, and ACT statistics (data-sources/:id/statistics). Each route
// renders a placeholder until its page epic lands.

export interface PageRoute {
  /** Path relative to /p/:slug/b/:branch. */
  path: string;
  title: string;
  /** The roadmap epic that builds the real page. */
  epic: string;
}

export const PROJECT_ROUTES: readonly PageRoute[] = [
  { path: 'dashboard', title: 'Dashboard', epic: 'P2' },
  { path: 'data-dictionary', title: 'Data Dictionary', epic: 'P2' },
  { path: 'data-elements', title: 'Data Elements', epic: 'P2' },
  { path: 'dictionary-quality', title: 'Dictionary Quality', epic: 'P2' },
  { path: 'element-details', title: 'Element Details', epic: 'P2' },
  { path: 'reference-lists', title: 'Reference Lists', epic: 'P2' },
  { path: 'reference-list-values', title: 'Reference List Values', epic: 'P2' },
  { path: 'reference-lists/:listId/values/:valueId', title: 'Reference Value', epic: 'P2' },
  { path: 'source-mappings', title: 'Source Mappings', epic: 'P2' },
  { path: 'source-mappings/:id', title: 'Source Mapping', epic: 'P2' },
  { path: 'data-sources', title: 'Data Sources', epic: 'P2' },
  { path: 'data-sources/:id', title: 'Data Source', epic: 'P2' },
  // Every data access policy of the branch, project-wide and per source (the owner, 2026-10-08).
  { path: 'access-policies', title: 'Access Policies', epic: 'P2' },
  { path: 'storages/:origin/:id', title: 'Storage', epic: 'P3' },
  { path: 'processing-rules', title: 'Processing Rules', epic: 'P2' },
  { path: 'data-review', title: 'Data Review', epic: 'P2' },
  { path: 'settings', title: 'Settings', epic: 'E2.1' },
  // The configuration master (ADR 0022): the runtime profile of every deployed environment.
  { path: 'settings/deployment', title: 'Deployment configuration', epic: 'E4.2' },
  // How a developer's AI tool connects to cortex (ADR 0030): the full guide behind Settings' Connect card.
  { path: 'settings/ai-tools', title: 'Connect an AI tool', epic: 'E4.2' },
  // The project's own AI (ADR 0032): its source, models, the fleet on it, its organization's providers.
  { path: 'settings/ai', title: 'AI', epic: 'E4.2' },
  { path: 'profile', title: 'User Profile', epic: 'E2.1' },
  { path: 'alerts', title: 'Alerts', epic: 'P2' },
  { path: 'branches', title: 'Branches', epic: 'E2.2' },
  // The repo, browsable like GitHub (tree, files, commits, branches, compare): pages/code.
  { path: 'code/*', title: 'Code', epic: 'E2.2' },
  { path: 'versions', title: 'Versions', epic: 'E2.6' },
  { path: 'audit-log', title: 'Audit Log', epic: 'E2.6' },
  { path: 'pull-requests/:id', title: 'Pull request', epic: 'E4.1' },
  { path: 'projects', title: 'Projects', epic: 'E2.1' },
  { path: 'organizations', title: 'Organizations', epic: 'E2.1' },
  { path: 'templates', title: 'Templates', epic: 'E4.2' },
  { path: 'fleet', title: 'Agent fleet', epic: 'P4' },
  // Every AI of the platform (ADR 0032), platform admins: usage, the registry, organizations, prices.
  { path: 'ai-gateway', title: 'AI gateway', epic: 'P4' },
  // The platform and its projects as 3D cities of their code (ADR 0042), platform admins.
  { path: 'code-city', title: 'Code City', epic: 'P4' },
  { path: 'cortex-audit', title: 'Cortex Audit', epic: 'P5' },
  { path: 'pipeline', title: 'Data', epic: 'P3' },
  { path: 'board', title: 'Board', epic: 'P4' },
  { path: 'feed', title: 'Feed', epic: 'P5' },
  { path: 'apps', title: 'Apps', epic: 'P4' },
  { path: 'apps/:viewName', title: 'App', epic: 'P4' },
  // An app's editor and live preview (ADR 0018 §6), reached from its ⋯ menu on Apps.
  { path: 'apps/:app/edit', title: 'Edit app', epic: 'P4' },
  { path: 'agents', title: 'Agents', epic: 'P5' },
  { path: 'agents/:agentId', title: 'Agent', epic: 'P5' },
  { path: 'guides', title: 'Guides', epic: 'P3' },
  // Who may use the project's app and which tenants each person sees (ADR 0066), its admins only.
  { path: 'access', title: 'Access', epic: 'P3' },
  { path: 'models', title: 'Models', epic: 'P3' },
  { path: 'models/:modelName/run', title: 'Model Run', epic: 'P3' },
  { path: 'workflows', title: 'Workflows', epic: 'E3.7' },
  { path: 'reports', title: 'Reports', epic: 'P3' },
  { path: 'audit', title: 'Audit', epic: 'P5' },
  { path: 'docs', title: 'Docs', epic: 'E2.3' },
  { path: 'deployments', title: 'Deployments', epic: 'P4' },
  { path: 'status', title: 'Status', epic: 'P3' },
  { path: 'structure', title: 'Structure', epic: 'E2.4' },
  { path: 'strategies', title: 'Strategies', epic: 'P3' },
  { path: 'strategies/:kind', title: 'Strategy', epic: 'P3' },
];

/** Project pages that are built (the rest render a placeholder), by path, with their module. */
export const BUILT_PAGES = {
  branches: () =>
    import('../modules/workspace/index.ts')
      .then((door) => door.pages['pages/Branches']())
      .then((m) => m.BranchesPage),
  'code/*': () =>
    import('../modules/source/index.ts')
      .then((door) => door.pages['code/CodePage']())
      .then((m) => m.CodePage),
  docs: () =>
    import('../modules/fleet/index.ts').then((door) => door.pages['pages/Docs']()).then((m) => m.DocsPage),
  organizations: () =>
    import('../modules/workspace/index.ts')
      .then((door) => door.pages['admin/Organizations']())
      .then((m) => m.OrganizationsPage),
  projects: () =>
    import('../modules/workspace/index.ts')
      .then((door) => door.pages['admin/Projects']())
      .then((m) => m.ProjectsPage),
  settings: () =>
    import('../modules/workspace/index.ts')
      .then((door) => door.pages['admin/Settings']())
      .then((m) => m.SettingsPage),
  'settings/deployment': () =>
    import('../modules/preview-host/index.ts')
      .then((door) => door.pages['deployment-config/DeploymentConfig']())
      .then((m) => m.DeploymentConfigPage),
  'settings/ai-tools': () =>
    import('../modules/ai/index.ts')
      .then((door) => door.pages['admin/ConnectGuide']())
      .then((m) => m.ConnectGuidePage),
  'settings/ai': () =>
    import('../modules/ai/index.ts')
      .then((door) => door.pages['pages/ProjectAiPage']())
      .then((m) => m.ProjectAiPage),
  'ai-gateway': () =>
    import('../modules/ai/index.ts')
      .then((door) => door.pages['pages/AiGatewayPage']())
      .then((m) => m.AiGatewayPage),
  'code-city': () =>
    import('../modules/fleet/index.ts').then((door) => door.pages['code-city']()).then((m) => m.CodeCityPage),
  profile: () =>
    import('../modules/identity/index.ts')
      .then((door) => door.pages['admin/Profile']())
      .then((m) => m.ProfilePage),
  'data-dictionary': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.DataDictionaryPage)),
  'data-elements': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.DataElementsPage)),
  'element-details': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.ElementDetailsPage)),
  'data-sources': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.DataSourcesPage)),
  'data-sources/:id': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.DataSourceViewPage)),
  'access-policies': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.AccessPoliciesPage)),
  'source-mappings': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.SourceMappingsPage)),
  'source-mappings/:id': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.SourceMappingViewPage)),
  'reference-lists': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.ReferenceListsPage)),
  'reference-list-values': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.ReferenceListValuesPage)),
  'reference-lists/:listId/values/:valueId': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.ReferenceValueDetailsPage)),
  'processing-rules': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.ProcessingRulesPage)),
  'data-review': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['pipeline']())
      .then((m) => m.pipelined(m.views.DataReviewPage)),
  'dictionary-quality': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.DictionaryQualityPage)),
  workflows: () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['workflows']())
      .then((m) => m.WorkflowsRoute),
  structure: () =>
    import('../modules/source/index.ts')
      .then((door) => door.pages['structure/Structure']())
      .then((m) => m.StructurePage),
  guides: () =>
    import('../modules/platform/index.ts').then((door) => door.pages['guides']()).then((m) => m.GuidesPage),
  access: () =>
    import('../modules/mini-apps/index.ts').then((door) => door.pages['access']()).then((m) => m.AccessPage),
  status: () =>
    import('../modules/fleet/index.ts')
      .then((door) => door.pages['status/Status']())
      .then((m) => m.StatusPage),
  pipeline: () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['pipeline']())
      .then((m) => m.pipelined(m.views.PipelinePage)),
  reports: () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['reports']())
      .then((m) => m.ReportsPage),
  models: () =>
    import('../modules/mini-apps/index.ts').then((door) => door.pages['models']()).then((m) => m.ModelsPage),
  'models/:modelName/run': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['models']())
      .then((m) => m.ModelRunPage),
  alerts: () =>
    import('../modules/workspace/index.ts').then((door) => door.pages['Alerts']()).then((m) => m.AlertsPage),
  versions: () =>
    import('../modules/source/index.ts').then((door) => door.pages['Versions']()).then((m) => m.VersionsPage),
  'pull-requests/:id': () =>
    import('../modules/source/index.ts')
      .then((door) => door.pages['repos/PullRequest']())
      .then((m) => m.PullRequestPage),
  'audit-log': () =>
    import('../modules/identity/index.ts')
      .then((door) => door.pages['AuditLog']())
      .then((m) => m.AuditLogPage),
  dashboard: () =>
    Promise.all([
      import('../modules/mini-apps/index.ts').then((door) => door.pages['governance']()),
      import('../modules/workspace/index.ts').then((door) => door.pages['dashboard/Dashboard']()),
    ]).then(([h, m]) => h.governed(m.DashboardPage)),
  'storages/:origin/:id': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['governance']())
      .then((m) => m.governed(m.views.StorageDetailPage)),
  strategies: () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['pipeline']())
      .then((m) => m.pipelined(m.views.StrategiesPage)),
  'strategies/:kind': () =>
    import('../modules/mini-apps/index.ts')
      .then((door) => door.pages['pipeline']())
      .then((m) => m.pipelined(m.views.StrategiesPage)),
  board: () =>
    import('../modules/fleet/index.ts').then((door) => door.pages['board/Board']()).then((m) => m.BoardPage),
  feed: () =>
    import('../modules/fleet/index.ts').then((door) => door.pages['feed/Feed']()).then((m) => m.FeedPage),
  templates: () =>
    import('../modules/source/index.ts')
      .then((door) => door.pages['templates/Templates']())
      .then((m) => m.TemplatesPage),
  apps: () =>
    import('../modules/mini-apps/index.ts').then((door) => door.pages['apps/Apps']()).then((m) => m.AppsPage),
  'apps/:viewName': () =>
    import('../modules/mini-apps/index.ts').then((door) => door.pages['apps/Apps']()).then((m) => m.AppsPage),
  'apps/:app/edit': () =>
    import('../modules/preview-host/index.ts')
      .then((door) => door.pages['prototype/PrototypePage']())
      .then((m) => m.PrototypePage),
  deployments: () =>
    import('../modules/deployments/index.ts')
      .then((door) => door.pages['deployments/Deployments']())
      .then((m) => m.DeploymentsPage),
  agents: () =>
    import('../modules/mini-apps/index.ts').then((door) => door.pages['agents']()).then((m) => m.AgentsRoute),
  'agents/:agentId': () =>
    import('../modules/mini-apps/index.ts').then((door) => door.pages['agents']()).then((m) => m.AgentRoute),
  fleet: () =>
    import('../modules/fleet/index.ts').then((door) => door.pages['fleet/Fleet']()).then((m) => m.FleetPage),
} as const satisfies Record<string, () => Promise<ComponentType>>;

/** Routes outside the project prefix. */
export const TOP_LEVEL_ROUTES = {
  login: '/login',
  mfa: '/mfa-verification',
  share: '/p/:slug/b/:branch/share/apps/:viewName',
} as const;

/** Old routes that are intentionally gone (listed, not silently missing). */
export const DROPPED_ROUTES = ['flow/*', 'flow-embed/*', 'benchmark', 'data-sources/:id/statistics'] as const;
